import {NsMissingCredentialsError, NsRestError, ResourceNotFoundError, RunConfigService} from '@nuskin/ns-util';
import {UserService} from '@nuskin/ns-account';

/**
 *
 * ServiceUtils
 *
 * @namespace ServiceUtils
 */
const ServiceUtils = {

    /**
   * Extracts the response body from the Fetch response
   * and transforms it to a JavaScript object.
   *
   * Promise rejects with {@link ResourceNotFoundError} if the server responds with a 404 error.
   * Promise rejects with {@link NsRestError} if the server responds any other kind of error.
   *
   * @memberof ServiceUtils
   * @example
   * ServiceUtils.transformFetchResponse()
   * @param {Response} response - Fetch response
   * @returns {Promise.<Object>} Promise that resolves to response data
   */
    transformFetchResponse: async (response) => {
        if (response.ok) {
            return response.json();
        }

        if (response.status === 404) {
            throw new ResourceNotFoundError();
        } else {
            let body = await response.json();
            throw NsRestError.fromRestResponse(body);
        }
    },

    /**
   * Returns the required endpoint headers for service calls
   * @memberof ServiceUtils
   * @example
   * ServiceUtils.getHeaders()
   * @returns {object} clientid/clientsecret object
   */
    getHeaders: () => {
        const runConfig = RunConfigService.getRunConfig();
        if (runConfig.clientId && runConfig.clientSecret) {
            return {
                'client_id': runConfig.clientId,
                'client_secret': runConfig.clientSecret,
                'Accept': 'application/json'
            };
        } else {
            throw new Error(`RunConfig not setup with clientId and clientSecret`);
        }
    },

    /**
   * Returns the User object for the current authenticated user.
   * @memberof ServiceUtils
   * @example
   * ServiceUtils.getUser()
   * @returns {User}
   * @throws NsMissingCredentialsError if the user is not authenticated.
   */
    getUser: () => {
        let user = UserService.getUser();
        if (!user) {
            throw new NsMissingCredentialsError();
        }
        return user;
    }
};

export default ServiceUtils;
